import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate as useGo } from "react-router-dom";
import searchIndex from "@/lib/__generated__/search-index.json";
import { trackSearch } from "@/lib/analytics";

type Item = {
  kind: "article" | "glossary" | "page";
  title: string;
  url: string;
  description?: string;
  keywords?: string[];
  section?: string;
};

const INDEX = searchIndex as Item[];

function score(item: Item, tokens: string[]): number {
  const title = item.title.toLowerCase();
  const desc = (item.description || "").toLowerCase();
  const kw = (item.keywords || []).join(" ").toLowerCase();
  let s = 0;
  for (const t of tokens) {
    if (!t) continue;
    if (title === t) s += 100;
    if (title.startsWith(t)) s += 40;
    if (title.includes(t)) s += 20;
    if (kw.includes(t)) s += 8;
    if (desc.includes(t)) s += 4;
  }
  return s;
}

function search(query: string, limit = 8): Item[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  const scored: { item: Item; s: number }[] = [];
  for (const item of INDEX) {
    const s = score(item, tokens);
    if (s > 0) scored.push({ item, s });
  }
  scored.sort((a, b) => b.s - a.s || a.item.title.localeCompare(b.item.title));
  return scored.slice(0, limit).map((x) => x.item);
}

export default function SearchBox() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const goTo = useGo();

  const results = useMemo(() => search(query, 8), [query]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    trackSearch(query, results.length);
  }, [query, results.length]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const go = useCallback(
    (url: string) => {
      setOpen(false);
      setQuery("");
      goTo(url);
    },
    [goTo],
  );

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length) setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (results.length) setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) go(results[active].url);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full sm:w-72" role="search">
      <label className="sr-only" htmlFor="site-search">
        Search the site
      </label>
      <div className="relative">
        <span
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          id="site-search"
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search this site"
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={open && results.length > 0}
          aria-activedescendant={
            open && results[active] ? `search-result-${active}` : undefined
          }
          role="combobox"
          className="w-full text-sm pl-9 pr-3 py-2 rounded-md"
          style={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            color: "hsl(var(--foreground))",
          }}
          data-testid="input-search"
        />
      </div>

      {open && query.trim() && (
        <div
          id="search-results"
          role="listbox"
          className="absolute right-0 left-0 mt-2 max-h-[70vh] overflow-auto"
          style={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderTop: "3px solid hsl(var(--brand))",
            borderRadius: "var(--radius)",
            boxShadow: "0 12px 32px -8px rgba(0,0,0,0.18)",
            zIndex: 60,
          }}
        >
          {results.length === 0 ? (
            <p
              className="px-4 py-3 text-sm"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              No matches. Try a different word.
            </p>
          ) : (
            <ul className="list-none m-0 p-0">
              {results.map((r, i) => (
                <li
                  key={r.url}
                  id={`search-result-${i}`}
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    go(r.url);
                  }}
                  className="px-4 py-2 cursor-pointer"
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid hsl(var(--border))",
                    background:
                      i === active ? "hsl(var(--secondary))" : "transparent",
                  }}
                  data-testid={`search-result-${i}`}
                >
                  <div
                    className="text-[14px] font-medium"
                    style={{ color: "hsl(var(--foreground))" }}
                  >
                    {r.title}
                  </div>
                  <div
                    className="text-[12px] uppercase tracking-widest mt-0.5"
                    style={{ color: "hsl(var(--accent-text))" }}
                  >
                    {r.section || r.kind}
                  </div>
                  {r.description && (
                    <div
                      className="text-[13px] leading-snug mt-1 line-clamp-2"
                      style={{ color: "hsl(var(--muted-foreground))" }}
                    >
                      {r.description}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
